import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) {
    return auth.currentUser;
  }

  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;

    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(null);
    }, timeoutMs);

    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export interface BaseHedgeMaterial {
  id: string;
  account_id: string;
  base_formula_id: string;
  name: string;
  is_default: boolean;
  base_contract_size_mt?: number;
  unit?: string;
  created_at: string;
  updated_at: string;
  formula_code: string;
  formula_name: string;
}

export interface DerivedHedgeMaterial {
  id: number;
  template_id: number;
  derived_formula_id: number;
  allocation_percent: number;
  price_ratio: number;
  linked_ratio_formula_id?: number | null;
  linked_ratio_code?: string | null;
  linked_ratio_name?: string | null;
  default_time_allocation_mode?: string;
  default_time_allocation_params?: any;
  sort_order: number;
  created_at: string;
  updated_at: string;
  formula_code: string;
  formula_name: string;
}

export interface CreateDerivedMaterialPayload {
  derived_formula_id: number;
  price_ratio: number;
  allocation_percent?: number | null;
  linked_ratio_formula_id?: number | null;
}

export interface UpdateDerivedMaterialPayload {
  price_ratio?: number;
  /** Ratio formula code → live daily ratio; null → fixed price_ratio; omitted → unchanged. */
  linked_ratio_code?: string | null;
  allocation_percentage?: number | null;
  is_active?: boolean;
}

export const baseHedgeMaterialsService = {
  getByAccount: async (accountId: number): Promise<BaseHedgeMaterial[]> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/v2/base-hedge-materials/account/${accountId}`, {
      headers,
    });
    if (!response.ok) {
      throw new Error('Failed to fetch base hedge materials');
    }
    return response.json();
  },

  getDerivedMaterials: async (baseHedgeMaterialId: number): Promise<DerivedHedgeMaterial[]> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/v2/base-hedge-materials/${baseHedgeMaterialId}/derived`, {
      headers,
    });
    if (!response.ok) {
      throw new Error('Failed to fetch derived materials');
    }
    return response.json();
  },

  createDerivedMaterial: async (
    baseHedgeMaterialId: number,
    payload: CreateDerivedMaterialPayload
  ): Promise<DerivedHedgeMaterial> => {
    const headers = await getAuthHeaders();
    console.log('[baseHedgeMaterialsService] Creating derived material:', {
      baseHedgeMaterialId,
      payload,
      url: `${API_URL}/api/v2/base-hedge-materials/${baseHedgeMaterialId}/derived`,
    });
    const response = await fetch(`${API_URL}/api/v2/base-hedge-materials/${baseHedgeMaterialId}/derived`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    console.log('[baseHedgeMaterialsService] Response:', {
      status: response.status,
      statusText: response.statusText,
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[baseHedgeMaterialsService] Error response:', errorText);
      throw new Error('Failed to create derived material');
    }
    return response.json();
  },

  updateDerivedMaterial: async (
    derivedMaterialId: number,
    payload: UpdateDerivedMaterialPayload
  ): Promise<DerivedHedgeMaterial> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/v2/base-hedge-materials/derived/${derivedMaterialId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error('Failed to update derived material');
    }
    return response.json();
  },

  deleteDerivedMaterial: async (derivedMaterialId: number): Promise<void> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/v2/base-hedge-materials/derived/${derivedMaterialId}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      throw new Error('Failed to delete derived material');
    }
  },

  /**
   * Update allocation percentages for multiple derived materials
   * Used by Strategy page to save calculated allocations
   */
  updateAllocations: async (
    updates: Array<{ derivedMaterialId: number; allocationPercent: number }>
  ): Promise<void> => {
    const headers = await getAuthHeaders();
    
    // Update each derived material with its allocation percentage
    const promises = updates.map(({ derivedMaterialId, allocationPercent }) =>
      fetch(`${API_URL}/api/v2/base-hedge-materials/derived/${derivedMaterialId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ allocation_percent: allocationPercent }),
      }).then(response => {
        if (!response.ok) {
          throw new Error(`Failed to update allocation for derived material ${derivedMaterialId}`);
        }
        return response.json();
      })
    );
    
    await Promise.all(promises);
  },

  /**
   * Update monthly allocation maps for multiple derived materials
   * Stores per-month allocation percentages in default_time_allocation_params JSONB field
   */
  updateMonthlyAllocations: async (
    updates: Array<{ derivedMaterialId: number; monthlyAllocations: Record<string, number> }>
  ): Promise<void> => {
    const headers = await getAuthHeaders();
    
    // Update each derived material with its monthly allocation map
    const promises = updates.map(({ derivedMaterialId, monthlyAllocations }) => {
      const payload = { 
        default_time_allocation_params: { monthlyAllocations } 
      };
      
      console.log('[updateMonthlyAllocations] Sending to API:', {
        derivedMaterialId,
        payload,
        sampleMonths: Object.keys(monthlyAllocations).slice(0, 3)
      });
      
      return fetch(`${API_URL}/api/v2/base-hedge-materials/derived/${derivedMaterialId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      }).then(async response => {
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[updateMonthlyAllocations] API error:', errorText);
          throw new Error(`Failed to update monthly allocations for derived material ${derivedMaterialId}: ${errorText}`);
        }
        const result = await response.json();
        console.log('[updateMonthlyAllocations] API response:', result);
        return result;
      });
    });
    
    await Promise.all(promises);
  },
};
